// WebGL inner-glow for text using alpha-based edge detection and simple blur

export function renderInnerGlow(sourceCanvas, options = {}, sharedGl) {
  const width = Math.max(1, sourceCanvas.width | 0);
  const height = Math.max(1, sourceCanvas.height | 0);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const gl =
    sharedGl ||
    canvas.getContext('webgl', { premultipliedAlpha: true }) ||
    canvas.getContext('experimental-webgl');
  if (!gl) return sourceCanvas;

  const {
    radius = 4.0, // blur radius in px
    intensity = 0.9, // 0..1
    color = [1.0, 1.0, 1.0],
    softness = 0.25, // 0..1 edge softness
  } = options || {};

  const vs = `
        attribute vec2 a_position;
        attribute vec2 a_texCoord;
        varying vec2 v_uv;
        void main(){ v_uv = a_texCoord; gl_Position = vec4(a_position,0.0,1.0); }
    `;
  const fs = `
        precision mediump float;
        varying vec2 v_uv;
        uniform sampler2D u_src;
        uniform vec2 u_texel;
        uniform float u_radiusPx;
        uniform float u_intensity;
        uniform vec3 u_color;
        uniform float u_softness;

        void main(){
            vec4 base = texture2D(u_src, v_uv);
            float a = base.a;
            vec2 step = u_texel * u_radiusPx;
            // 9-tap box blur of alpha (cheap and fast)
            float b = 0.0;
            b += texture2D(u_src, v_uv + vec2(-step.x, -step.y)).a;
            b += texture2D(u_src, v_uv + vec2(0.0,      -step.y)).a;
            b += texture2D(u_src, v_uv + vec2(step.x,   -step.y)).a;
            b += texture2D(u_src, v_uv + vec2(-step.x,  0.0)).a;
            b += texture2D(u_src, v_uv).a;
            b += texture2D(u_src, v_uv + vec2(step.x,   0.0)).a;
            b += texture2D(u_src, v_uv + vec2(-step.x,  step.y)).a;
            b += texture2D(u_src, v_uv + vec2(0.0,       step.y)).a;
            b += texture2D(u_src, v_uv + vec2(step.x,    step.y)).a;
            b /= 9.0;

            // Edge intensity near interior border
            float edge = (1.0 - b) * a; // strong near inner edge
            edge = smoothstep(0.0, clamp(0.6 + 0.4*u_softness, 0.0, 1.0), edge);

            vec3 glow = u_color * edge * u_intensity;
            // screen-like blend for glow
            vec3 screened = 1.0 - (1.0 - base.rgb) * (1.0 - glow);
            vec3 rgb = mix(base.rgb, screened, edge);
            gl_FragColor = vec4(rgb, a);
        }
    `;

  const vert = _createShader(gl, gl.VERTEX_SHADER, vs);
  const frag = _createShader(gl, gl.FRAGMENT_SHADER, fs);
  if (!vert || !frag) return sourceCanvas;
  const prog = _createProgram(gl, vert, frag);
  if (!prog) return sourceCanvas;
  gl.useProgram(prog);

  const pos = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, pos);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
    gl.STATIC_DRAW
  );
  const uv = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, uv);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([0, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 0]),
    gl.STATIC_DRAW
  );

  const aPos = gl.getAttribLocation(prog, 'a_position');
  const aUv = gl.getAttribLocation(prog, 'a_texCoord');
  gl.bindBuffer(gl.ARRAY_BUFFER, pos);
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindBuffer(gl.ARRAY_BUFFER, uv);
  gl.enableVertexAttribArray(aUv);
  gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 0, 0);

  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sourceCanvas);

  const uSrc = gl.getUniformLocation(prog, 'u_src');
  const uTexel = gl.getUniformLocation(prog, 'u_texel');
  const uRadius = gl.getUniformLocation(prog, 'u_radiusPx');
  const uInt = gl.getUniformLocation(prog, 'u_intensity');
  const uCol = gl.getUniformLocation(prog, 'u_color');
  const uSoft = gl.getUniformLocation(prog, 'u_softness');

  gl.uniform1i(uSrc, 0);
  gl.uniform2f(uTexel, 1 / width, 1 / height);
  gl.uniform1f(uRadius, radius);
  gl.uniform1f(uInt, Math.max(0, Math.min(1, intensity)));
  gl.uniform3f(uCol, color[0] || 0, color[1] || 0, color[2] || 0);
  gl.uniform1f(uSoft, Math.max(0, Math.min(1, softness)));

  gl.viewport(0, 0, width, height);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.drawArrays(gl.TRIANGLES, 0, 6);

  return sharedGl ? gl.canvas : canvas;
}

function _createShader(gl, type, src) {
  const s = gl.createShader(type);
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error('TextFX inner-glow shader error:', gl.getShaderInfoLog(s));
    gl.deleteShader(s);
    return null;
  }
  return s;
}
function _createProgram(gl, vs, fs) {
  const p = gl.createProgram();
  gl.attachShader(p, vs);
  gl.attachShader(p, fs);
  gl.linkProgram(p);

  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    console.error('TextFX inner-glow program error:', gl.getProgramInfoLog(p));
    gl.deleteProgram(p);
    return null;
  }
  return p;
}
